import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

// Safe localStorage wrappers for iOS compatibility
function safeLocalStorageGet(key: string): string | null {
    try {
        return localStorage.getItem(key)
    } catch (error) {
        console.warn(`[AuthService] localStorage.getItem('${key}') failed:`, error)
        return null
    }
}

function safeLocalStorageSet(key: string, value: string): boolean {
    try {
        localStorage.setItem(key, value)
        return true
    } catch (error) {
        console.warn(`[AuthService] localStorage.setItem('${key}') failed:`, error)
        return false
    }
}

function safeLocalStorageRemove(key: string): boolean {
    try {
        localStorage.removeItem(key)
        return true
    } catch (error) {
        console.warn(`[AuthService] localStorage.removeItem('${key}') failed:`, error)
        return false
    }
}

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = safeLocalStorageGet('access_token')
                if (token && token !== 'undefined' && token !== 'null') {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )

        // Add response interceptor to handle 401 errors
        apiInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    // Clear auth data and redirect to login
                    safeLocalStorageRemove('access_token')
                    safeLocalStorageRemove('user')
                    window.location.href = '/sign-in'
                }
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export interface LoginCredentials {
    username: string
    password: string
}

export interface RegisterData {
    email: string
    username: string
    password: string
    first_name?: string
    last_name?: string
    role?: string
}

export interface User {
    id: string
    email: string
    username: string
    first_name?: string
    last_name?: string
    role: string
    is_active: boolean
}

export interface AuthResponse {
    access_token: string
    token_type: string
    user: User
}

export const authService = {
    login,
    register,
    logout,
    getCurrentUser,
    verifyToken,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    checkUsernameAvailability,
    checkEmailExists,
    forgotPassword,
    validateResetToken,
    resetPassword,
}

/**
 * Login with username and password
 */
async function login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    try {
        const response = await getApiInstance().post('/auth/login', credentials)
        
        // Backend wraps response in ResponseModel: { success, message, data }
        // The actual token data is in response.data.data
        const responseData = response.data
        const tokenData = responseData.data || response.data // Support both wrapped and unwrapped
        
        // Log the response for debugging
        // Validate response structure
        if (!tokenData || !tokenData.access_token) {
            // Check if backend sent an error message
            const errorMsg = responseData?.message || tokenData?.message || tokenData?.error || tokenData?.detail || 'Invalid response from server: missing access_token'
            throw new Error(errorMsg)
        }
        
        if (!tokenData.user) {
            const errorMsg = responseData?.message || tokenData?.message || tokenData?.error || tokenData?.detail || 'Invalid response from server: missing user data'
            throw new Error(errorMsg)
        }
        
        // Store token and user data
        safeLocalStorageSet('access_token', tokenData.access_token)
        safeLocalStorageSet('user', JSON.stringify(tokenData.user))
        
        return {
            success: true,
            data: tokenData
        }
    } catch (error: any) {
        console.error('Login error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
        })
        
        // Clear any partial auth data on error
        safeLocalStorageRemove('access_token')
        safeLocalStorageRemove('user')
        
        // Extract error message from various possible locations
        let errorMessage = 'Login failed'
        
        if (error.response) {
            // The request was made and the server responded with an error status
            const responseData = error.response.data
            
            // Try to extract error message from common backend response formats
            errorMessage = 
                responseData?.detail ||  // FastAPI HTTPException uses 'detail'
                responseData?.message || 
                responseData?.error ||
                responseData?.msg ||
                (typeof responseData === 'string' ? responseData : null) ||
                `Server error: ${error.response.status}`
        } else if (error.request) {
            // The request was made but no response was received
            errorMessage = 'Unable to connect to server. Please check your internet connection.'
        } else {
            // Something else happened
            errorMessage = error.message || 'An unexpected error occurred'
        }
        
        return {
            success: false,
            error: errorMessage
        }
    }
}

/**
 * Register a new user
 */
async function register(userData: RegisterData): Promise<ApiResponse<any>> {
    try {
        const response = await getApiInstance().post('/auth/register', userData)
        return {
            success: true,
            data: response.data
        }
    } catch (error: any) {
        console.error('Registration error:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Registration failed'
        }
    }
}

/**
 * Logout user
 */
async function logout(): Promise<void> {
    // Clear authentication data from localStorage
    safeLocalStorageRemove('access_token')
    safeLocalStorageRemove('user')

    // Redirect to sign-in page
    window.location.href = '/sign-in'
}

/**
 * Get current user from localStorage
 */
function getCurrentUser(): User | null {
    try {
        const userStr = safeLocalStorageGet('user')

        // Check if we have a valid string (not null, not "undefined", not "null", not empty)
        if (!userStr || userStr === 'undefined' || userStr === 'null' || userStr.trim() === '') {
            // Clean up invalid localStorage entries
            if (userStr && (userStr === 'undefined' || userStr === 'null')) {
                safeLocalStorageRemove('user')
            }
            return null
        }

        // Attempt to parse the JSON
        const user = JSON.parse(userStr)

        // Validate that we got an actual object with required properties
        if (!user || typeof user !== 'object' || !user.id || !user.role) {
            safeLocalStorageRemove('user')
            return null
        }

        return user
    } catch (error) {
        console.error('Error getting current user:', error)
        // Clean up corrupted data
        safeLocalStorageRemove('user')
        return null
    }
}

/**
 * Verify if token is valid
 */
async function verifyToken(): Promise<boolean> {
    try {
        const token = safeLocalStorageGet('access_token')
        if (!token || token === 'undefined' || token === 'null') {
            return false
        }

        const response = await getApiInstance().get('/auth/verify')
        return response.status === 200
    } catch (error) {
        return false
    }
}

/**
 * Check if user is authenticated
 */
function isAuthenticated(): boolean {
    const token = safeLocalStorageGet('access_token')
    const user = getCurrentUser()

    // Check that both token and user exist and are valid
    if (!token || token === 'undefined' || token === 'null') {
        return false
    }

    return !!(token && user)
}

/**
 * Check if user has specific role
 */
function hasRole(role: string): boolean {
    const user = getCurrentUser()
    return user?.role === role
}

/**
 * Check if user has any of the specified roles
 */
function hasAnyRole(roles: string[]): boolean {
    const user = getCurrentUser()
    return roles.some(role => user?.role === role)
}

/**
 * Check if a username is available
 */
async function checkUsernameAvailability(username: string): Promise<ApiResponse<{ available: boolean; username: string }>> {
    try {
        const response = await getApiInstance().get(`/auth/check-username/${username}`)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Username check error:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to check username'
        }
    }
}

/**
 * Check if a user with the given email exists
 */
async function checkEmailExists(email: string): Promise<ApiResponse<{ exists: boolean; email: string }>> {
    try {
        const response = await getApiInstance().get(`/auth/check-email/${email}`)
        return {
            success: true,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Email check error:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to check email'
        }
    }
}

/**
 * Request password reset email
 */
async function forgotPassword(email: string): Promise<ApiResponse<{ email: string }>> {
    try {
        const response = await getApiInstance().post('/auth/forgot-password', { email })
        return {
            success: true,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Forgot password error:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to send reset email'
        }
    }
}

/**
 * Validate password reset token
 */
async function validateResetToken(token: string): Promise<ApiResponse<{ valid: boolean; email?: string }>> {
    try {
        const response = await getApiInstance().post('/auth/validate-reset-token', { token })
        return {
            success: response.data.success,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Validate reset token error:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Invalid or expired token'
        }
    }
}

/**
 * Reset password with token
 */
async function resetPassword(token: string, newPassword: string): Promise<ApiResponse<{ reset: boolean }>> {
    try {
        const response = await getApiInstance().post('/auth/reset-password', { 
            token, 
            new_password: newPassword 
        })
        return {
            success: response.data.success,
            data: response.data.data
        }
    } catch (error: any) {
        console.error('Reset password error:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.response?.data?.detail || error.message || 'Failed to reset password'
        }
    }
}